import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Globe, MapPin, Truck, User } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardContent } from "semantic-ui-react";
import { getBulkDropdownData, getDropdownData } from "../../api/keyvalue";
import { addLocation, updateLocation } from "../../api/locationCreationApi";
import ContactComponent from "../ContactComponent";
import Button from "../input/Button";
import {
  locationContactValidation,
  locationCustomerValidation,
  locationGpsProviderValidation,
  locationValidation,
} from "../validations/locationCreation";
import LocationCustomer from "./LocationCustomer";
import LocationForm from "./LocationForm";
import LocationGpsProvider from "./LocationGpsProvider";
import { errorActions } from "../../store/error-slice";
import Tab from "../Tab";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";
import Modal from "../Modal";

const AddLocation = ({
  currentValues,
  editMode,
  setEditMode,
  setCurrentValues,
}) => {
  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.LOCATION,
  });
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Dropdown
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [locTypeOptions, setLocTypeOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [titleOptions, setTitleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const contactRef = useRef();
  const locationRef = useRef();
  const locationCustomerRef = useRef();
  const gpsProviderRef = useRef();

  const user = useSelector((state) => state.user);
  const errorState = useSelector((state) => state.error.locationCreationErrors);
  const dispatch = useDispatch();

  const [locationData, setLocationData] = useState(
    editMode ? currentValues.location.locName : "",
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      console.log("fetching dropdown");

      //new

      const {
        active,
        location_approval: status,
        loc_type: locTypeOpt,
        contact: contactOpt,
        title: titleOpt,
        departments: departmentsOpt,
      } = await getBulkDropdownData([
        { key: "active" },
        {
          key: "location_approval",
          authenticated: true,
          authorities: [
            "LOCATION_CUSTOMER_FORM_APPROVE",
            "LOCATION_CUSTOMER_FORM_REJECT",
            "LOCATION_CUSTOMER_FORM_CUSTOMER_APPROVE",
          ],
        },
        { key: "loc_type" },
        { key: "contact" },
        { key: "title" },
        { key: "departments" },
      ]);

      setActiveOptions(active);
      setStatusOptions(status);
      setLocTypeOptions(locTypeOpt);
      setTypeOptions(contactOpt);
      setTitleOptions(titleOpt);
      setDepartmentOptions(departmentsOpt);

      console.log("finished fetching dropdown");
      setIsLoading(false);
    })();
  }, []);

  const reset = () => {
    locationRef.current.resetForm();
    locationCustomerRef.current.resetForm();
    gpsProviderRef.current.resetForm();
    contactRef.current.resetForm();

    setEditMode(false);
    setCurrentValues(null);
  };

  const [tab, setTab] = useState(firstViableTab);
  const { next, prev } = useNextAndPreviousAuthority({
    sectionKey: "location",
    minTab: 1,
    maxTab: 4,
    module: MODULES.Master,
    section: FORMS.LOCATION,
    currentTab: tab,
    authenticated: true,
  });
  const nextButtonHandler = () => {
    switch (tab) {
      case 1: {
        const locErrors = locationRef.current.validateForm();
        console.log("loc errors", locErrors);
        if (Object.keys(locErrors).length > 0) {
          dispatch(
            errorActions.addLocationCreationErrorObject({
              form: "locationErrors",
              errors: locErrors,
            }),
          );
          toast.error("There was an error adding Location", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else {
          dispatch(errorActions.clearLocationCreationError());
        }
        break;
      }
      case 2: {
        const isContactDetailsInvalid = contactRef.current.validateForm();
        if (isContactDetailsInvalid) {
          dispatch(
            errorActions.addLocationCreationError({
              form: "contactErrors",
              key: "mainContactError",
              value: "Should have at least one main contact detail",
            }),
          );
          toast.error("There should be at least 1 main contact", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        }
        break;
      }
      case 3: {
        const customerErrors = locationCustomerRef.current.validateForm();
        if (Object.keys(customerErrors).length > 0) {
          dispatch(
            errorActions.addLocationCreationErrorObject({
              form: "locationCustomerErrors",
              errors: customerErrors,
            }),
          );
          toast.error("There should be at least 1 Location Customer", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else {
          dispatch(errorActions.clearLocationCreationError());
        }
        break;
      }
      default:
    }
    console.log("NEXT -> ", next());
    setTab(next());
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();
    if (!bypassDeleteCheck && locationRef.current.isDeletedActive()) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }
    console.log("submit");
    setIsSubmitLoading(true);
    const locationErrors = locationValidation(locationRef.current.getData());
    const contactErrors = locationContactValidation(
      contactRef.current.getData(),
    );
    const locationCustomerErrors = locationCustomerValidation(
      locationCustomerRef.current.getData(),
    );
    const locationGpsProviderErrors = locationGpsProviderValidation(
      gpsProviderRef.current.getData(),
    );

    const contactDetails = contactRef.current.getData();
    let mainContacts;

    if (editMode) {
      const { modified, created, unmodified } = contactDetails;
      console.log("Contact Details when updating:", contactDetails);

      const allContacts = [...modified, ...created, ...unmodified];

      mainContacts = allContacts.filter((contact) => contact.conType === 1);
    } else {
      mainContacts = contactDetails.filter((contact) => contact.conType === 1);
    }

    if (mainContacts.length === 0) {
      contactErrors.mainContactError = "At least one main contact is required";
      toast.error("There should be at least 1 main contact", {
        autoClose: 5000,
        containerId: "Main",
      });
    }

    if (
      Object.keys(locationErrors).length > 0 ||
      Object.keys(contactErrors).length > 0 ||
      Object.keys(locationCustomerErrors).length > 0 ||
      Object.keys(locationGpsProviderErrors).length > 0
    ) {
      dispatch(
        errorActions.validateLocationCreation({
          locationData: locationRef.current.getData(),
          contactData: contactRef.current.getData(),
          locationCustomer: locationCustomerRef.current.getData(),
          locationGpsProvider: gpsProviderRef.current.getData(),
        }),
      );
      if (Object.keys(locationErrors).length > 0) {
        toast.error("There was an error adding Location", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      setIsSubmitLoading(false);
      return;
    } else {
      try {
        if (editMode) {
          await updateLocation({
            location: locationRef.current.getData(),
            contactDetails,
            locationCustomer: locationCustomerRef.current.getData(),
            locationGpsLocations: gpsProviderRef.current.getData(),
            author: user.username,
          });
          reset();
          dispatch(errorActions.clearLocationCreationError());

          toast.success("location updated successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          await addLocation({
            location: locationRef.current.getData(),
            contactDetails,
            locationCustomer: locationCustomerRef.current.getData(),
            locationGpsLocations: gpsProviderRef.current.getData(),
            author: user.username,
          });
          reset();
          dispatch(errorActions.clearLocationCreationError());
          toast.success("location created successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
      } catch (e) {
        if (e?.response?.status === 409) {
          toast.error("A location with the same GPS provider already exists!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          toast.error("There was an error", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
      }
      setIsSubmitLoading(false);
    }
  };

  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div styles={{ width: "80vw", boxShadow: "none" }}>
        <Card className={"stakeholder-card-container"}>
          <CardContent>
            <div className="ui top attached tabular menu">
              <Tab
                Icon={MapPin}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                errors={Object.keys(errorState.locationErrors).length > 0}
                label="Location"
                authenticated={true}
                form={FORMS.LOCATION.LOC_DETAIL.form}
                resizeForSmallerScreens={true}
                showMandatory={true}
                isMandatory={true}
              />
              <Tab
                Icon={User}
                onClick={() => {
                  setTab(2);
                }}
                active={tab === 2}
                errors={Object.keys(errorState.contactErrors).length > 0}
                label="Contact"
                authenticated={true}
                form={FORMS.LOCATION.LOC_CONTACTS.form}
                resizeForSmallerScreens={true}
                showMandatory={true}
                isMandatory={true}
              />
              <Tab
                Icon={Globe}
                onClick={() => {
                  setTab(3);
                }}
                active={tab === 3}
                label="Location Customer"
                authenticated={true}
                form={FORMS.LOCATION.LOC_CUSTOMER.form}
                resizeForSmallerScreens={true}
                showMandatory={true}
                isMandatory={true}
                errors={
                  Object.keys(errorState.locationCustomerErrors).length > 0
                }
              />
              <Tab
                Icon={Truck}
                onClick={() => {
                  setTab(4);
                }}
                active={tab === 4}
                label="Location Provider"
                authenticated={true}
                form={FORMS.LOCATION.LOC_PROVIDER.form}
                resizeForSmallerScreens={true}
              />
            </div>
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              <LocationForm
                // errors={errors}
                errors={errorState.locationErrors}
                dropdown={{ locTypeOptions, statusOptions, activeOptions }}
                currentValues={
                  editMode && currentValues !== null
                    ? currentValues.location
                    : null
                }
                editMode={editMode}
                setCurrentLocationData={setLocationData}
                isLoading={isLoading}
                ref={locationRef}
              />
            </div>
            {/* Tab2 */}
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              <ContactComponent
                opeType={5}
                ref={contactRef}
                useDynamicFields={true}
                type={1}
                keyValue={"loc-type"}
                dropdown={{
                  typeOptions,
                  titleOptions,
                  departmentOptions,
                  statusOptions,
                  activeOptions,
                }}
                currentValues={
                  editMode && currentValues !== null
                    ? currentValues.contactDetails
                    : null
                }
                editMode={editMode}
                isLoading={isLoading}
                errors={errorState.contactErrors}
              />
            </div>
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 3 ? "block" : "none" }}
            >
              <LocationCustomer
                errors={errorState.locationCustomerErrors}
                dropdown={{ activeOptions, statusOptions }}
                isLoading={isLoading}
                ref={locationCustomerRef}
                locationData={locationData}
                currentValues={
                  editMode && currentValues !== null
                    ? currentValues.locationCustomer
                    : null
                }
                editMode={editMode}
              />
            </div>
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 4 ? "block" : "none" }}
            >
              <LocationGpsProvider
                errors={errorState.locationGpsProviderErrors}
                dropdown={{ activeOptions }}
                isLoading={isLoading}
                locationData={locationData}
                ref={gpsProviderRef}
                currentValues={
                  editMode && currentValues !== null
                    ? currentValues.locationGpsLocations
                    : null
                }
                editMode={editMode}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              {tab > firstViableTab && (
                <Button
                  template="back"
                  tab={tab - 1}
                  module={MODULES.Master}
                  section={FORMS.LOCATION}
                  authenticated={true}
                  onClick={() => setTab(prev())}
                />
              )}

              {tab < lastViableTab && (
                <Button
                  template="next"
                  tab={tab + 1}
                  module={MODULES.Master}
                  section={FORMS.LOCATION}
                  authenticated={true}
                  onClick={nextButtonHandler}
                />
              )}

              {tab === lastViableTab && !isLoading && (
                <Button
                  template="create"
                  onClick={handleSubmit}
                  isLoading={isSubmitLoading}
                  form={FORMS.LOCATION.LOC.form}
                  module={MODULES.Master}
                  section={FORMS.LOCATION}
                  authenticated={true}
                  editMode={editMode}
                  create={!editMode}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <Modal ref={deleteWarningRef} closeButton={false} minWidth={"300px"}>
        {showDeleteWarning && deleteWarningModal}
      </Modal>
    </>
  );
};

export default AddLocation;
